import React, { useState } from 'react'
import moment from 'moment'
import { Link } from 'react-router-dom'
import { getMessage } from 'lib/translator'
import { TABLE_ACTIONS } from 'containers/ListingPage'
import { SingleDatePicker, TimePicker } from 'components/Form'
import { Dialog } from 'components/Popup'
import { DropDown, DropDownItem, ICONS } from 'components/DropDown'
import { Cell } from 'components/Table'
import { Validator } from 'jsonschema'
import API from 'lib/api'

const DATE_FORMAT = 'D MMM YYYY'

const STATUS_OPTIONS = [
  {
    value: true,
    text: getMessage(`apphome.enabled`),
  },
  {
    value: false,
    text: getMessage(`apphome.disabled`),
  },
]

const TableDropdown = ({
  id,
  type,
  isEnable,
  onAction,
  setAction,
  editLink,
}) => {
  return (
    <div>
      <DropDown icon={<img src={ICONS.VELLIP} alt="⋮" />}>
        {!['tiles'].includes(type) && (
          <DropDownItem
            onClick={() => {
              setAction('confirmation')
            }}
          >
            {isEnable
              ? getMessage('apphome.disable')
              : getMessage('apphome.enable')}
          </DropDownItem>
        )}
        <DropDownItem>
          <Link to={editLink}>{getMessage('apphome.edit')}</Link>
        </DropDownItem>
        {id !== `${type}-Default` && (
          <DropDownItem onClick={() => onAction(TABLE_ACTIONS.DELETE, { id })}>
            {getMessage('apphome.delete')}
          </DropDownItem>
        )}
      </DropDown>
    </div>
  )
}

const TableAction = ({ isEnable, onAction, id, type, editLink }) => {
  const [errorMessage, setErrorMessage] = useState(null)
  const [action, setAction] = useState(null)

  const handleStatus = () => {
    setAction(null)
    let url = `/genie/lms/${type}/${id}/status`

    if (type === 'splash') {
      url = `/genie/lms/splashscreen/${id}/status`
    }

    return new API({
      url,
    })
      .patch({ isEnable: !isEnable })
      .then(() => onAction(TABLE_ACTIONS.EDIT, { id }, { isEnable: !isEnable }))
      .catch((error) => {
        if (error) {
          setErrorMessage(getMessage('apphome.errorMessage.contact-team'))
          setAction('error')
        }
      })
  }

  return (
    <div>
      <Dialog
        show={action === 'error'}
        information={errorMessage}
        close={() => setAction(null)}
        closeText={getMessage('apphome.okay')}
      />
      <Dialog
        show={action === 'confirmation'}
        title={getMessage('apphome.confirmation')}
        onOk={handleStatus}
        close={() => setAction(null)}
        closeText={getMessage('apphome.cancel')}
        okText={getMessage('apphome.yes')}
      />
      <TableDropdown
        id={id}
        type={type}
        isEnable={isEnable}
        onAction={onAction}
        setAction={setAction}
        editLink={editLink}
      />
    </div>
  )
}

const ColumnName = ({ name, link }) => {
  return (
    <Cell className="column-campaign">
      <Link to={link}>{name}</Link>
    </Cell>
  )
}

const ColumnDate = ({ type, time }) => {
  return (
    <Cell className={`column-${type}-date`}>
      {time === '' ? '-' : moment(time).format(DATE_FORMAT)}
    </Cell>
  )
}

const ColumnStatus = ({ status }) => {
  return <Cell className={`column-${status}`}>{status}</Cell>
}

const getStatus = (startAt, endAt) => {
  let status
  if (moment().isAfter(endAt)) {
    status = getMessage('apphome.expired')
  } else if (moment().isBefore(startAt)) {
    status = getMessage('apphome.scheduled')
  } else {
    status = getMessage('apphome.live')
  }
  return status
}

const filterAppHomeListing = (params) => {
  if (params.status) {
    if (params.status === 'Live') {
      delete params.status
      params.preview = true
    } else {
      delete params.preview
    }
  }
  return params
}

const DatePickerField = ({
  label,
  placeholder,
  readOnlyField,
  generateStateMappers,
  required = true,
}) => {
  return (
    <SingleDatePicker
      enableToday
      className="w-100"
      required={required}
      label={getMessage(`apphome.${label}`)}
      placeholder={getMessage(`apphome.${placeholder}`)}
      {...generateStateMappers({
        stateKeys: [label === 'validFrom' ? 'startDate' : 'endDate'],
        loseEmphasisOnFill: true,
      })}
      disabled={readOnlyField}
    />
  )
}

const TimePickerField = ({
  label,
  readOnlyField,
  generateStateMappers,
  placeholder = label,
  required = true,
}) => {
  return (
    <TimePicker
      required={required}
      className="w-100"
      label={getMessage(`apphome.${label}`)}
      placeholder={getMessage(`apphome.${placeholder}`)}
      {...generateStateMappers({
        stateKeys: [label === 'startTime' ? 'startTime' : 'endTime'],
        loseEmphasisOnFill: true,
      })}
      disabled={readOnlyField}
    />
  )
}

const closeErrorMsgPopup = (_this) => {
  _this.setState({
    showErrorDialog: false,
    errorMessage: null,
  })
}

const formatDateTime = (date, time) => {
  return `${moment(date, 'DD MM YYYY')._i}T${
    moment(time || '00:00:00', 'HH:mm:ss')._i
  }+08:00`
}

const formatValidity = async (source, data) => {
  let result
  let hasValidity

  if (source === 'api') {
    hasValidity = data.startTime && data.endTime
    result = {
      startTime: hasValidity
        ? moment(data.startTime).format('HH:mm:ss')
        : '00:00:00',
      endTime: hasValidity
        ? moment(data.endTime).format('HH:mm:ss')
        : '00:00:00',
      startDate: hasValidity
        ? moment(data.startTime).format('YYYY-MM-DD')
        : null,
      endDate: hasValidity ? moment(data.endTime).format('YYYY-MM-DD') : null,
    }
  }

  if (source === 'form') {
    hasValidity = data.startDate && data.endDate
    result = {
      startTime: hasValidity
        ? formatDateTime(data.startDate, data.startTime)
        : null,
      endTime: hasValidity ? formatDateTime(data.endDate, data.endTime) : null,
    }
  }

  return result
}

const jsonParser = (analytic) => {
  try {
    return JSON.parse(analytic)
  } catch (error) {
    return null
  }
}

const validateAnalytics = (data, schema) => {
  const parsedAnalytic = jsonParser(data)
  const validator = new Validator()
  const result = validator.validate(parsedAnalytic, schema)

  return {
    isValid: result.valid,
    errors: result.errors,
    parsedAnalytic,
  }
}

const contentAPIRequestHandler = async (
  req,
  payload,
  callbacks,
  query = ''
) => {
  let response
  const api = new API({ url: `/genie/lms/content` + query })

  try {
    if (req === 'GET') {
      response = await api.get()
    }

    if (req === 'POST') {
      response = await api.post(payload)
    }

    if (req === 'PUT') {
      response = await api.put(payload.data)
    }

    if (req === 'PATCH') {
      response = await api.patch(payload)
    }

    if (req === 'DELETE') {
      response = await api.delete(payload)
    }

    if (response) {
      callbacks.onSuccess(response)
    }
  } catch (error) {
    callbacks.onError(error)
  } finally {
    callbacks.onCancel()
  }
}

export {
  ColumnName,
  ColumnDate,
  ColumnStatus,
  getStatus,
  formatDateTime,
  filterAppHomeListing,
  DatePickerField,
  TimePickerField,
  closeErrorMsgPopup,
  TableDropdown,
  TableAction,
  formatValidity,
  validateAnalytics,
  contentAPIRequestHandler,
  DATE_FORMAT,
  STATUS_OPTIONS,
}
